import { useState, useEffect, useRef, useCallback } from 'react';
import { aiIntelligenceService, JobState, JobStatusResult } from '../services/aiIntelligenceService';

const POLL_INTERVAL_MS = 2500; // Poll every 2.5 seconds
const MAX_POLL_DURATION_MS = 120000; // 120 seconds max polling

type GenerationPhase = 'idle' | 'submitting' | 'polling' | 'completed' | 'failed' | 'timeout';

interface UsePlanGenerationReturn {
  phase: GenerationPhase;
  jobState: JobState | null;
  planId: string | null;
  errorMessage: string | null;
  retryable: boolean;
  startGeneration: (teamId: string) => void;
  retry: () => void;
  reset: () => void;
}

/**
 * Returns a contextual progress message based on the current job state.
 */
function getProgressMessage(jobState: JobState | null, elapsedMs: number): string {
  if (!jobState || jobState === 'queued') {
    if (elapsedMs < 5000) return 'Analysing assessment data...';
    return 'Building context from your coaching DNA...';
  }
  if (jobState === 'processing') {
    if (elapsedMs < 15000) return 'Generating your development plan...';
    if (elapsedMs < 40000) return 'Structuring weekly themes and sessions...';
    if (elapsedMs < 80000) return 'Finalising sessions and recommendations...';
    return 'Almost there — validating output...';
  }
  return '';
}

export { getProgressMessage };

export function usePlanGeneration(): UsePlanGenerationReturn {
  const [phase, setPhase] = useState<GenerationPhase>('idle');
  const [jobState, setJobState] = useState<JobState | null>(null);
  const [, setJobId] = useState<string | null>(null);
  const [planId, setPlanId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [retryable, setRetryable] = useState(false);
  const [lastTeamId, setLastTeamId] = useState<string | null>(null);

  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeRef = useRef<number>(0);
  const cancelledRef = useRef(false);

  const cleanup = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    cancelledRef.current = true;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  const pollJobStatus = useCallback(async (currentJobId: string) => {
    if (cancelledRef.current) return;

    const elapsed = Date.now() - startTimeRef.current;
    if (elapsed >= MAX_POLL_DURATION_MS) {
      setPhase('timeout');
      setErrorMessage('Plan generation is taking longer than expected. Please try again later.');
      setRetryable(true);
      return;
    }

    try {
      const status: JobStatusResult = await aiIntelligenceService.getJobStatus(currentJobId);
      if (cancelledRef.current) return;

      setJobState(status.state);

      if (status.state === 'completed' && status.planId) {
        setPlanId(status.planId);
        setPhase('completed');
        return;
      }

      if (status.state === 'failed') {
        setPhase('failed');
        setErrorMessage(
          status.failureReason?.message || 'Plan generation failed. Please try again.'
        );
        setRetryable(status.failureReason?.retryable !== false);
        return;
      }

      if (status.state === 'cancelled') {
        setPhase('failed');
        setErrorMessage('Plan generation was cancelled.');
        setRetryable(true);
        return;
      }

      // Continue polling for queued/processing states
      pollTimerRef.current = setTimeout(() => pollJobStatus(currentJobId), POLL_INTERVAL_MS);
    } catch (err: any) {
      if (cancelledRef.current) return;
      setPhase('failed');
      setErrorMessage(
        err.response?.data?.error?.message || 'Unable to check generation status. Please try again.'
      );
      setRetryable(true);
    }
  }, []);

  const startGeneration = useCallback(async (teamId: string) => {
    cleanup();
    cancelledRef.current = false;
    setPhase('submitting');
    setJobState(null);
    setJobId(null);
    setPlanId(null);
    setErrorMessage(null);
    setRetryable(false);
    setLastTeamId(teamId);

    try {
      const newJobId = await aiIntelligenceService.generatePlan(teamId);
      if (cancelledRef.current) return;

      setJobId(newJobId);
      setPhase('polling');
      startTimeRef.current = Date.now();
      pollTimerRef.current = setTimeout(() => pollJobStatus(newJobId), POLL_INTERVAL_MS);
    } catch (err: any) {
      if (cancelledRef.current) return;
      setPhase('failed');
      const status = err.response?.status;
      if (status === 429) {
        setErrorMessage(
          err.response?.data?.error?.message || 'Rate limit reached. Please try again later.'
        );
        setRetryable(false);
      } else {
        setErrorMessage(
          err.response?.data?.error?.message || 'Failed to start plan generation. Please try again.'
        );
        setRetryable(true);
      }
    }
  }, [cleanup, pollJobStatus]);

  const retry = useCallback(() => {
    if (lastTeamId) {
      startGeneration(lastTeamId);
    }
  }, [lastTeamId, startGeneration]);

  const reset = useCallback(() => {
    cleanup();
    cancelledRef.current = false;
    setPhase('idle');
    setJobState(null);
    setJobId(null);
    setPlanId(null);
    setErrorMessage(null);
    setRetryable(false);
  }, [cleanup]);

  return {
    phase,
    jobState,
    planId,
    errorMessage,
    retryable,
    startGeneration,
    retry,
    reset,
  };
}
